import React, { useEffect, useRef } from 'react';
import { X, Download, Printer, Copy, Check } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

interface ReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    title?: string;
    query?: string;
}

export const ReportModal: React.FC<ReportModalProps> = ({ isOpen, onClose, children, title, query }) => {
    const { currentUser } = useAppContext();
    const [copySuccess, setCopySuccess] = React.useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    // Close on Escape key
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    // Prevent body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    const handlePrint = () => {
        if (!contentRef.current) return;

        // Open a new window
        const printWindow = window.open('', '_blank', 'width=900,height=800');
        if (!printWindow) return;

        // Gather all styles (stylesheets and inline styles) from the main document
        // This ensures Tailwind and other component styles are carried over
        const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
            .map(node => node.outerHTML)
            .join('');

        const content = contentRef.current.innerHTML;

        const html = `
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <title>Print Report - ${title || 'Executive Analysis'}</title>
                ${styles}
                <style>
                    body { 
                        background-color: white !important; 
                        color: black !important; 
                        padding: 40px; 
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                    }
                    /* Ensure scrollable containers show full content in print */
                    .overflow-y-auto { overflow: visible !important; height: auto !important; }
                    
                    /* Force light mode colors for readability */
                    .dark .text-white { color: #111827 !important; }
                    .dark .bg-gray-800, .dark .bg-gray-900 { background-color: white !important; }
                    .text-gray-300 { color: #4b5563 !important; } /* Darken light gray text */
                    
                    /* Table adjustments */
                    table { width: 100% !important; border-collapse: collapse; }
                    th, td { border: 1px solid #e5e7eb; page-break-inside: avoid; }
                </style>
            </head>
            <body>
                <div class="max-w-4xl mx-auto">
                    <!-- Header -->
                    <div class="border-b-2 border-gray-800 pb-6 mb-8">
                        <div class="flex items-center justify-between mb-4">
                            <h1 class="text-3xl font-bold text-gray-900">${title || 'Executive Analysis Report'}</h1>
                            <span class="text-4xl">📊</span>
                        </div>
                        
                        <div class="grid grid-cols-2 gap-4 text-sm text-gray-600">
                            <div>
                                <p><span class="font-semibold">Date:</span> ${formattedDate}</p>
                                <p><span class="font-semibold">Time:</span> ${formattedTime}</p>
                            </div>
                            <div class="text-right">
                                <p><span class="font-semibold">Requested by:</span> ${currentUser?.username || 'User'}</p>
                                <p><span class="font-semibold">System:</span> AutoGain DfR</p>
                            </div>
                        </div>

                        ${query ? `
                        <div class="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p class="text-sm text-gray-800"><span class="font-bold">Query:</span> ${query}</p>
                        </div>
                        ` : ''}
                    </div>

                    <!-- Main Content -->
                    <div class="report-content space-y-6">
                        ${content}
                    </div>

                    <!-- Footer -->
                    <div class="mt-12 pt-6 border-t border-gray-200 text-xs text-gray-500 text-center">
                        <p>Generated by AutoGain DfR System | ${formattedDate}</p>
                        <p class="mt-1 italic">⚠️ AI-Generated Content: Please verify all data, especially FMEA tables and RPN calculations.</p>
                    </div>
                </div>

                <script>
                    // Wait for resources to load then print
                    window.onload = function() {
                        setTimeout(() => {
                            window.print();
                        }, 500);
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(html);
        printWindow.document.close();
    };

    const handleExport = () => {
        if (!contentRef.current) return;

        const reportContent = contentRef.current.innerText;
        const blob = new Blob([reportContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `DfR-Report-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handleCopy = async () => {
        if (!contentRef.current) return;

        try {
            await navigator.clipboard.writeText(contentRef.current.innerText);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    if (!isOpen) return null;

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const formattedTime = currentDate.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
    });

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fadeIn print:hidden"
                onClick={onClose}
            />

            {/* Modal */}
            <div
                id="printable-modal"
                className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none print:z-auto print:p-0"
            >
                <div
                    className="
                        relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl
                        w-full max-w-5xl max-h-[90vh]
                        flex flex-col
                        pointer-events-auto
                        animate-slideUp
                        print:max-w-full print:max-h-none print:shadow-none print:rounded-none- important
                    "
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-t-2xl print:rounded-none print:bg-none print:border-b-2 print:border-black">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg print:hidden">
                                    <span className="text-2xl">📊</span>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white print:text-black">
                                        {title || 'Executive Analysis Report'}
                                    </h2>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 print:text-gray-600">
                                        Generated by <span className="font-semibold">AutoGain DfR System</span>
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 ml-14 print:ml-0 print:text-black">
                                <span>📅 {formattedDate} at {formattedTime}</span>
                                <span>👤 Requested by: <span className="font-medium">{currentUser?.username || 'User'}</span></span>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 print:hidden">
                            <button
                                onClick={handleCopy}
                                className="
                                    p-2 rounded-lg
                                    bg-white dark:bg-gray-800
                                    hover:bg-blue-50 dark:hover:bg-blue-900/20
                                    text-gray-600 dark:text-gray-400
                                    hover:text-blue-600 dark:hover:text-blue-400
                                    transition-all duration-200
                                    shadow-sm hover:shadow-md
                                    group
                                "
                                title="Copy to clipboard"
                            >
                                {copySuccess ? (
                                    <Check size={20} className="text-green-600" />
                                ) : (
                                    <Copy size={20} className="group-hover:scale-110 transition-transform" />
                                )}
                            </button>
                            <button
                                onClick={handleExport}
                                className="
                                    p-2 rounded-lg
                                    bg-white dark:bg-gray-800
                                    hover:bg-green-50 dark:hover:bg-green-900/20
                                    text-gray-600 dark:text-gray-400
                                    hover:text-green-600 dark:hover:text-green-400
                                    transition-all duration-200
                                    shadow-sm hover:shadow-md
                                    group
                                "
                                title="Download as text"
                            >
                                <Download size={20} className="group-hover:scale-110 transition-transform" />
                            </button>
                            <button
                                onClick={handlePrint}
                                className="
                                    p-2 rounded-lg
                                    bg-white dark:bg-gray-800
                                    hover:bg-purple-50 dark:hover:bg-purple-900/20
                                    text-gray-600 dark:text-gray-400
                                    hover:text-purple-600 dark:hover:text-purple-400
                                    transition-all duration-200
                                    shadow-sm hover:shadow-md
                                    group
                                "
                                title="Print report"
                            >
                                <Printer size={20} className="group-hover:scale-110 transition-transform" />
                            </button>
                            <button
                                onClick={onClose}
                                className="
                                    p-2 rounded-xl
                                    bg-white dark:bg-gray-800
                                    hover:bg-red-50 dark:hover:bg-red-900/20
                                    text-gray-600 dark:text-gray-400
                                    hover:text-red-600 dark:hover:text-red-400
                                    transition-all duration-200
                                    shadow-md hover:shadow-lg
                                    group
                                "
                                aria-label="Close modal"
                            >
                                <X size={24} className="group-hover:rotate-90 transition-transform duration-300" />
                            </button>
                        </div>
                    </div>

                    {/* User Query Display */}
                    {query && (
                        <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900/10 border-b border-blue-200 dark:border-blue-800 print:bg-gray-50 print:border-gray-300 print:text-black">
                            <p className="text-sm text-gray-700 dark:text-gray-300 print:text-black">
                                <span className="font-semibold">Query:</span> {query}
                            </p>
                        </div>
                    )}

                    {/* Scrollable Content */}
                    <div ref={contentRef} className="flex-1 overflow-y-auto px-6 py-6 custom-scrollbar print:overflow-visible print:h-auto print:block">
                        {children}
                    </div>

                    {/* Footer with Disclaimer */}
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-2xl print:hidden space-y-3">
                        {/* LLM Disclaimer */}
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
                            <div className="flex items-start gap-2">
                                <span className="text-yellow-600 dark:text-yellow-400 text-lg flex-shrink-0">⚠️</span>
                                <div className="text-xs text-yellow-800 dark:text-yellow-200">
                                    <p className="font-semibold mb-1">AI-Generated Content Notice</p>
                                    <p>
                                        This report was generated using a Large Language Model (LLM) and should be reviewed for accuracy.
                                        Please verify all data, especially FMEA tables, RPN calculations, and recommendations, against your
                                        official design documentation before making critical decisions.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-between items-center print:hidden">
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                Press <kbd className="px-2 py-1 bg-white dark:bg-gray-700 rounded border border-gray-300 dark:border-gray-600 text-xs font-mono">Esc</kbd> to close
                            </p>
                            <button
                                onClick={onClose}
                                className="
                                    px-4 py-2 rounded-lg
                                    bg-gradient-to-r from-blue-500 to-indigo-600
                                    text-white font-medium
                                    hover:shadow-lg hover:scale-105
                                    transition-all duration-200
                                "
                            >
                                Close Report
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom scrollbar and animation styles */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 12px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgb(243 244 246);
                    border-radius: 10px;
                }
                .dark .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgb(31 41 55);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: linear-gradient(to bottom, rgb(59 130 246), rgb(99 102 241));
                    border-radius: 10px;
                    border: 2px solid rgb(243 244 246);
                }
                .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                    border-color: rgb(31 41 55);
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(to bottom, rgb(37 99 235), rgb(79 70 229));
                }

                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </>
    );
};

export default ReportModal;
